import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Check, Plus, Trash2 } from "lucide-react";

// Custom UI Components
const InputField = ({ label, helper, ...props }) => (
  <div className="space-y-1.5 flex-1">
    {label && <label className="text-sm font-semibold text-gray-700">{label}</label>}
    <input
      autoComplete="off"
      className="w-full px-4 py-3 bg-[#f9fafb] border border-[#e5e5e5] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/50 focus:border-[#5b6f00] transition-all hover:bg-white focus:bg-white"
      {...props}
    />
    {helper && <p className="text-xs text-gray-500">{helper}</p>}
  </div>
);

const TextAreaField = ({ label, helper, ...props }) => (
  <div className="space-y-1.5 flex-1 w-full">
    {label && <label className="text-sm font-semibold text-gray-700">{label}</label>}
    <textarea
      className="w-full px-4 py-3 bg-[#f9fafb] border border-[#e5e5e5] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/50 focus:border-[#5b6f00] transition-all hover:bg-white focus:bg-white min-h-[100px] resize-y"
      {...props}
    />
    {helper && <p className="text-xs text-gray-500">{helper}</p>}
  </div>
);

const SelectField = ({ label, options, ...props }) => (
  <div className="space-y-1.5 flex-1">
    {label && <label className="text-sm font-semibold text-gray-700">{label}</label>}
    <div className="relative">
      <select
        className="w-full px-4 py-3 bg-[#f9fafb] border border-[#e5e5e5] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/50 focus:border-[#5b6f00] transition-all hover:bg-white focus:bg-white appearance-none"
        {...props}
      >
        <option value="" disabled>Select an option</option>
        {options.map((opt) => (
          <option key={opt.value || opt} value={opt.value || opt}>
            {opt.label || opt}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
        <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
    </div>
  </div>
);

const TagInput = ({ label, tags, setTags, placeholder, helper }) => {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim() && !tags.includes(inputValue.trim())) {
        setTags([...tags.filter(t => t), inputValue.trim()]);
        setInputValue("");
      }
    }
  };

  const removeTag = (indexToRemove) => {
    setTags(tags.filter((_, i) => i !== indexToRemove));
  };

  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-semibold text-gray-700">{label}</label>}
      <div className="flex flex-wrap gap-2 p-3 bg-[#f9fafb] border border-[#e5e5e5] rounded-2xl focus-within:ring-2 focus-within:ring-[#5b6f00]/50 focus-within:border-[#5b6f00] focus-within:bg-white transition-all hover:bg-white min-h-[56px] items-center">
        {tags.filter(t => t).map((tag, i) => (
          <motion.span
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            key={tag}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f7f6f1] text-[#5b6f00] text-sm font-medium rounded-xl border border-[#e5e5e5]"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(i)}
              className="text-gray-400 hover:text-red-500 transition-colors p-0.5 rounded-full"
            >
              <X size={14} />
            </button>
          </motion.span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? placeholder : "Type and press Enter..."}
          className="flex-1 min-w-[150px] bg-transparent outline-none py-1 px-1 text-sm text-gray-800 placeholder-gray-400"
        />
      </div>
      {helper && <p className="text-xs text-gray-500">{helper}</p>}
    </div>
  );
};

const ItineraryDayCard = ({ day, index, updateDay, removeDay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
    className="bg-white p-6 md:p-8 rounded-3xl border border-[#e5e5e5] shadow-sm space-y-6 relative group transition-all hover:shadow-md hover:border-[#5b6f00]/30"
  >
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-bold text-[#1f2d1f] flex items-center gap-2">
        <span className="bg-[#f7f6f1] text-[#5b6f00] w-8 h-8 rounded-full flex items-center justify-center text-sm">
          {day.day}
        </span>
        Day {day.day}
      </h3>
      {index > 0 && (
        <button
          type="button"
          onClick={() => removeDay(index)}
          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors opacity-0 group-hover:opacity-100"
        >
          <Trash2 size={18} />
        </button>
      )}
    </div>

    <div className="space-y-4">
      <TextAreaField
        label="🌅 Morning"
        placeholder="What's the plan for the morning?"
        value={day.morning}
        onChange={(e) => updateDay(index, "morning", e.target.value)}
      />
      <TextAreaField
        label="☀️ Afternoon"
        placeholder="Where to eat? What to see?"
        value={day.afternoon}
        onChange={(e) => updateDay(index, "afternoon", e.target.value)}
      />
      <TextAreaField
        label="🌙 Evening"
        placeholder="Nightlife, dinner, or relaxing?"
        value={day.evening}
        onChange={(e) => updateDay(index, "evening", e.target.value)}
      />
      <TextAreaField
        label="💡 Insider Tips"
        placeholder="Any reservations needed? Dress codes?"
        value={day.tips}
        onChange={(e) => updateDay(index, "tips", e.target.value)}
      />
    </div>
  </motion.div>
);

export default function CreateTrip() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  const [loading, setLoading] = useState(false);

  // BASIC
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");

  // EXPERIENCE
  const [highlights, setHighlights] = useState([]);
  const [audience, setAudience] = useState("Solo");
  const [travelStyle, setTravelStyle] = useState("Relaxed");

  // PLANNING
  const [itinerary, setItinerary] = useState([
    { day: 1, morning: "", afternoon: "", evening: "", tips: "" },
  ]);
  const [thingsToAvoid, setThingsToAvoid] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  // COST
  const [budget, setBudget] = useState("");
  const [budgetBreakdown, setBudgetBreakdown] = useState("");
  const [bestTime, setBestTime] = useState("");

  // EXTRA
  const [duration, setDuration] = useState("");
  const [stay, setStay] = useState("hotel");
  const [tags, setTags] = useState([]);
  const [misc, setMisc] = useState("");

  // LOAD DRAFT
  useEffect(() => {
    const draft = localStorage.getItem("tripDraft");
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed.title) setTitle(parsed.title);
        if (parsed.city) setCity(parsed.city);
        if (parsed.description) setDescription(parsed.description);
        if (parsed.highlights) setHighlights(parsed.highlights.filter(Boolean));
        if (parsed.audience) setAudience(parsed.audience);
        if (parsed.travelStyle) setTravelStyle(parsed.travelStyle);
        if (parsed.itinerary?.length > 0) setItinerary(parsed.itinerary);
        if (parsed.thingsToAvoid) setThingsToAvoid(parsed.thingsToAvoid.filter(Boolean));
        if (parsed.recommendations) setRecommendations(parsed.recommendations.filter(Boolean));
        if (parsed.budget) setBudget(parsed.budget);
        if (parsed.budgetBreakdown) setBudgetBreakdown(parsed.budgetBreakdown);
        if (parsed.bestTime) setBestTime(parsed.bestTime);
        if (parsed.duration) setDuration(parsed.duration);
        if (parsed.stay) setStay(parsed.stay);
        if (parsed.tags) setTags(parsed.tags.filter(Boolean));
        if (parsed.misc) setMisc(parsed.misc);
      } catch (e) {
        console.error("Failed to parse draft", e);
      }
    }
  }, []);

  // SAVE DRAFT
  useEffect(() => {
    const draft = {
      title, city, description, highlights, audience, travelStyle,
      itinerary, thingsToAvoid, recommendations, budget, budgetBreakdown,
      bestTime, duration, stay, tags, misc
    };
    localStorage.setItem("tripDraft", JSON.stringify(draft));
  }, [
    title, city, description, highlights, audience, travelStyle,
    itinerary, thingsToAvoid, recommendations, budget, budgetBreakdown,
    bestTime, duration, stay, tags, misc
  ]);

  const addDay = () =>
    setItinerary([
      ...itinerary,
      { day: itinerary.length + 1, morning: "", afternoon: "", evening: "", tips: "" },
    ]);

  const updateDay = (i, field, val) => {
    const updated = [...itinerary];
    updated[i][field] = val;
    setItinerary(updated);
  };

  const removeDay = (i) => {
    const updated = itinerary.filter((_, index) => index !== i);
    updated.forEach((day, idx) => {
      day.day = idx + 1;
    });
    setItinerary(updated);
  };

  const isStepValid = () => {
    if (currentStep === 1) {
      return title.trim().length > 0 && city.trim().length > 0 && description.trim().length > 0;
    }
    return true;
  };

  // SUBMIT
  const handleCreate = async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/curated-trips`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title,
            city,
            description,
            highlights,
            audience,
            travelStyle,
            itinerary: { days: itinerary },
            thingsToAvoid,
            recommendations,
            budget,
            budgetBreakdown,
            bestTime,
            duration,
            stay,
            tags,
            misc,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        alert(data.message);
        setLoading(false);
        return;
      }

      localStorage.removeItem("tripDraft");
      navigate("/curator/dashboard");
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
      setLoading(false);
    }
  };

  const stepVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-[#1f2d1f]">
                Let's start with the basics
              </h1>
              <p className="text-gray-500 text-lg">
                Give your trip a catchy title and tell us where you're going.
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-[#e5e5e5] space-y-6">
              <InputField
                label="Trip Title"
                placeholder="e.g. 3 Days of Food & Culture in Tokyo"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <InputField
                label="City"
                placeholder="Where does this take place?"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
              <TextAreaField
                label="Description"
                placeholder="What makes this trip special? Why should people take it?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-[#1f2d1f]">
                What's the vibe?
              </h1>
              <p className="text-gray-500 text-lg">
                Set expectations for travelers taking this trip.
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-[#e5e5e5] space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                <SelectField
                  label="Who is this for?"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  options={["Solo", "Couple", "Friends", "Family"]}
                />
                <SelectField
                  label="Travel Style"
                  value={travelStyle}
                  onChange={(e) => setTravelStyle(e.target.value)}
                  options={["Relaxed", "Packed", "Adventure", "Luxury"]}
                />
              </div>
              <TagInput
                label="Top Highlights"
                tags={highlights}
                setTags={setHighlights}
                placeholder="e.g. Sushi, Temples, Nightlife"
                helper="Type a highlight and press Enter"
              />
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-[#1f2d1f]">
                Build the itinerary
              </h1>
              <p className="text-gray-500 text-lg">
                Break down the trip day by day. Leave blanks if you want it loose.
              </p>
            </div>

            <div className="space-y-6">
              <AnimatePresence>
                {itinerary.map((day, i) => (
                  <ItineraryDayCard
                    key={day.day}
                    day={day}
                    index={i}
                    updateDay={updateDay}
                    removeDay={removeDay}
                  />
                ))}
              </AnimatePresence>

              <button
                type="button"
                onClick={addDay}
                className="w-full py-5 border-2 border-dashed border-[#5b6f00]/30 text-[#5b6f00] rounded-3xl font-semibold hover:bg-[#5b6f00]/5 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={20} /> Add Another Day
              </button>
            </div>

            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-[#e5e5e5] space-y-6 mt-8">
              <h3 className="text-xl font-bold text-[#1f2d1f]">Advice & Warnings</h3>
              <TagInput
                label="Recommendations (Do's)"
                tags={recommendations}
                setTags={setRecommendations}
                placeholder="e.g. Book museum tickets in advance"
              />
              <TagInput
                label="Things to Avoid (Don'ts)"
                tags={thingsToAvoid}
                setTags={setThingsToAvoid}
                placeholder="e.g. Tourist trap restaurants near the main square"
              />
            </div>
          </>
        );
      case 4:
        return (
          <>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-[#1f2d1f]">
                Cost & Logistics
              </h1>
              <p className="text-gray-500 text-lg">
                Help travelers prepare their wallets and calendars.
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-[#e5e5e5] space-y-6">
              <InputField
                label="Estimated Budget (₹)"
                type="number"
                placeholder="e.g. 15000"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
              />
              <TextAreaField
                label="Budget Breakdown"
                placeholder="How is this money spent? (e.g. Food: ₹5000, Stay: ₹10000)"
                value={budgetBreakdown}
                onChange={(e) => setBudgetBreakdown(e.target.value)}
              />
              <InputField
                label="Best Time to Visit"
                placeholder="e.g. October to March"
                value={bestTime}
                onChange={(e) => setBestTime(e.target.value)}
              />
            </div>
          </>
        );
      case 5:
        return (
          <>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-[#1f2d1f]">
                Final Touches
              </h1>
              <p className="text-gray-500 text-lg">
                Wrap it up with a few more details.
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-[#e5e5e5] space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                <InputField
                  label="Duration (days)"
                  type="number"
                  placeholder="e.g. 3"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                />
                <SelectField
                  label="Primary Stay Type"
                  value={stay}
                  onChange={(e) => setStay(e.target.value)}
                  options={[
                    { value: "hotel", label: "Hotel" },
                    { value: "hostel", label: "Hostel" },
                    { value: "luxury", label: "Luxury Resort" },
                    { value: "airbnb", label: "Airbnb / Rental" },
                  ]}
                />
              </div>
              <TagInput
                label="Categorization Tags"
                tags={tags}
                setTags={setTags}
                placeholder="e.g. BudgetFriendly, Foodie, Architecture"
                helper="This helps users find your trip via search."
              />
              <TextAreaField
                label="Miscellaneous Notes"
                placeholder="Add anything that doesn’t fit above — hacks, personal advice, etc."
                value={misc}
                onChange={(e) => setMisc(e.target.value)}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f6f1] text-[#1f2d1f] flex flex-col font-sans">
      {/* Top Navbar / Stepper */}
      <header className="sticky top-0 z-50 bg-[#f7f6f1]/80 backdrop-blur-md border-b border-[#e5e5e5]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-black/5 rounded-full transition-colors"
            >
              <X size={24} className="text-gray-600" />
            </button>
            <span className="font-semibold text-lg text-gray-800">Create a Trip</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-sm font-medium text-gray-500 bg-white px-4 py-2 rounded-full border border-[#e5e5e5] shadow-sm">
            <span>Step {currentStep} of {totalSteps}</span>
          </div>
        </div>
        {/* Progress Bar */}
        <div className="h-1 bg-gray-200/50 w-full">
          <div
            className="h-full bg-[#5b6f00] transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 md:px-6 py-12 pb-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="space-y-8"
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Bottom Sticky Action Bar */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-[#e5e5e5] p-4 md:p-6 shadow-[0_-4px_20px_rgba(0,0,0,0.04)] z-50">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <button
            onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
            className={`px-6 py-3.5 rounded-2xl font-medium transition-all flex items-center gap-2
              ${
                currentStep === 1
                  ? "invisible"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }
            `}
          >
            <ChevronLeft size={20} /> <span className="hidden sm:inline">Back</span>
          </button>

          {currentStep < totalSteps ? (
            <button
              onClick={() => setCurrentStep((prev) => Math.min(totalSteps, prev + 1))}
              disabled={!isStepValid()}
              className="px-8 py-3.5 rounded-2xl font-semibold bg-gray-900 text-white hover:bg-black transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95 shadow-md shadow-gray-900/10"
            >
              Next <ChevronRight size={20} />
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={!isStepValid() || loading}
              className="w-full sm:w-auto px-10 py-3.5 rounded-2xl font-semibold bg-[#5b6f00] text-white hover:bg-[#4a5a00] shadow-xl shadow-[#5b6f00]/30 transition-all flex items-center justify-center gap-2 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Publishing...
                </span>
              ) : (
                <>
                  <Check size={20} /> Publish Trip 🚀
                </>
              )}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}